/* eslint-disable */
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Search, Receipt, Pencil, Send, Trash2, RefreshCcw, Users, XCircle } from "lucide-react";
import { ChatDrawer } from "../../components/ChatDrawer";
import { ChangeCustomerDialog } from "../../components/ChangeCustomerDialog";
import OrderRowActions from "../../components/OrderRowActions";
import { OrderChangeRequestDialog, CR_STATUS_BADGE, changeLabel } from "../../components/OrderChangeRequestDialog";
import { OrderOriginalCompare } from "../../components/OrderOriginalCompare";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { useLang } from "../../context/LangContext";
import { API, useFmt, statusLabelMap, STATUS_COLOR } from "./_shared";

const EDITABLE_STATUSES = ["pending_agent", "pending", "confirmed", "postponed", "in_preparation", "ready"];

const OrderDetailDialog = ({ order, onClose, onChanged }) => {
  const { t } = useLang();
  const fm = useFmt();
  const navigate = useNavigate();
  const [changeCustOpen, setChangeCustOpen] = useState(false);
  if (!order) return null;

  const cancelOrder = async () => {
    const reason = window.prompt(`Annullare l'ordine ${order.order_number}?\n\nMotivo (facoltativo):`);
    if (reason === null) return;
    try {
      await axios.patch(`${API}/orders/${order.id}/status`, { status: "cancelled", cancellation_reason: reason || "" });
      toast.success("Ordine annullato");
      onChanged?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore annullamento");
    }
  };

  return (
    <Dialog open={!!order} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl bg-white max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <span>Ordine</span>
            <span className="font-mono text-[#8A1C2A]">{order.order_number}</span>
            <Badge className={STATUS_COLOR[order.status]}>{statusLabelMap(t)[order.status]}</Badge>
          </DialogTitle>
        </DialogHeader>
        {EDITABLE_STATUSES.includes(order.status) && (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => setChangeCustOpen(true)} className="border-stone-300 text-stone-700" data-testid="agent-change-customer-btn">
              <Users className="w-4 h-4 mr-1" /> Cambia cliente
            </Button>
            <Button size="sm" variant="outline" onClick={cancelOrder} className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="agent-cancel-order-btn">
              <XCircle className="w-4 h-4 mr-1" /> Annulla ordine
            </Button>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <Card className="bg-stone-50 border-stone-200 p-3">
            <p className="text-xs font-semibold text-stone-500 uppercase mb-2">Cliente</p>
            <p className="font-semibold">{order.customer?.customer_name}</p>
            {order.customer?.company && <p className="text-stone-600">{order.customer.company}</p>}
            <p className="text-xs text-stone-500 mt-1">P.IVA: {order.customer?.vat_number}</p>
            {order.customer?.sdi_code && <p className="text-xs text-stone-500">SDI: {order.customer.sdi_code}</p>}
            <p className="mt-2 text-xs"><Mail className="w-3 h-3 inline mr-1" />{order.customer?.email}</p>
            <p className="text-xs"><Phone className="w-3 h-3 inline mr-1" />{order.customer?.phone}</p>
            <p className="text-xs"><MapPin className="w-3 h-3 inline mr-1" />{order.customer?.address} {order.customer?.city}</p>
            {order.customer_id && (
              <button
                onClick={() => navigate(`/agent/statements?customer_id=${order.customer_id}`)}
                className="mt-2 text-xs text-[#8A1C2A] underline font-semibold text-left"
                data-testid="order-detail-customer-statements-link"
              >
                <Receipt className="w-3 h-3 inline mr-1" />Situazione contabile cliente →
              </button>
            )}
          </Card>
          <Card className="bg-stone-50 border-stone-200 p-3">
            <p className="text-xs font-semibold text-stone-500 uppercase mb-2">Info</p>
            <p>Data: {new Date(order.created_at).toLocaleString("it-IT")}</p>
            {order.arrival_date && <p>Arrivo merce: <strong>{order.arrival_date}</strong></p>}
            {order.requested_shipping_date && <p className="text-amber-700">🚚 Consegna richiesta: <strong>{new Date(order.requested_shipping_date).toLocaleDateString("it-IT")}</strong></p>}
            {order.shipping_date && <p>Spedizione: <strong>{order.shipping_date}</strong></p>}
            {order.expected_delivery_date && <p>Consegna prevista: <strong>{order.expected_delivery_date}</strong></p>}
            {order.document_reference && <p>📄 Rif. documento: <strong>{order.document_reference}</strong></p>}
            <p className="mt-2">Imponibile (IVA escl.): <strong>{fm(((order.final_subtotal_amount ?? order.subtotal_amount ?? order.total_amount) || 0))}</strong></p>
            <p>IVA: {fm(((order.final_vat_total ?? order.vat_total) || 0))}</p>
            <p>Totale (IVA incl.): <strong className="text-[#8A1C2A]">{fm(((order.final_grand_total ?? order.grand_total ?? order.total_amount) || 0))}</strong></p>
            <p className="text-[#C5A059]">Provvigione: <strong>{fm(((order.final_commission_amount ?? order.commission_amount) || 0))}</strong> ({((order.commission_rate || 0) * 100).toFixed(1)}%)</p>
          </Card>
        </div>

        {order.customer?.special_requests && (
          <div className="bg-[#C5A059]/10 border-l-4 border-[#C5A059] p-3 rounded">
            <p className="text-xs font-semibold mb-1">{t.pAgent_specialRequests}</p>
            <p className="text-sm whitespace-pre-wrap">{order.customer.special_requests}</p>
          </div>
        )}
        {order.delivery_notes && (
          <div className="bg-amber-50 border-l-4 border-amber-400 p-3 rounded" data-testid="agent-order-delivery-notes">
            <p className="text-xs font-semibold text-amber-800 mb-1">📝 Note ordine / consegna</p>
            <p className="text-sm whitespace-pre-wrap">{order.delivery_notes}</p>
          </div>
        )}
        {order.warehouse_notes && (
          <div className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded">
            <p className="text-xs font-semibold mb-1">Note magazzino</p>
            <p className="text-sm whitespace-pre-wrap">{order.warehouse_notes}</p>
          </div>
        )}

        {(order.change_requests || []).length > 0 && (
          <div className="border border-stone-200 rounded-lg overflow-hidden" data-testid="order-change-requests-history">
            <p className="bg-stone-50 px-3 py-2 text-xs font-semibold text-stone-700">🔁 Richieste di modifica</p>
            <div className="divide-y divide-stone-100 text-sm">
              {[...order.change_requests].reverse().map((r) => (
                <div key={r.id} className="p-3">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span className="text-xs text-stone-500">{new Date(r.requested_at).toLocaleString("it-IT")}</span>
                    <Badge className={CR_STATUS_BADGE[r.status]?.cls}>{CR_STATUS_BADGE[r.status]?.label}</Badge>
                  </div>
                  <ul className="mt-1 text-xs text-stone-700 space-y-0.5">
                    {r.changes.map((c, i) => <li key={i}>{changeLabel(c)}</li>)}
                  </ul>
                  {r.note && <p className="text-xs italic text-stone-500 mt-1">📝 {r.note}</p>}
                  {r.resolution_note && <p className="text-xs text-red-600 mt-1">Risposta magazzino: {r.resolution_note}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        <Card className="bg-white border-stone-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-stone-100">
              <tr>
                <th className="text-left p-2 text-xs font-semibold">Codice</th>
                <th className="text-left p-2 text-xs font-semibold">Descrizione</th>
                <th className="text-right p-2 text-xs font-semibold">Ord.</th>
                <th className="text-right p-2 text-xs font-semibold">Reale</th>
                <th className="text-right p-2 text-xs font-semibold">€/UM</th>
                <th className="text-right p-2 text-xs font-semibold">Tot.</th>
                <th className="text-right p-2 text-xs font-semibold text-[#C5A059]">Provv.%</th>
                <th className="text-right p-2 text-xs font-semibold text-[#C5A059]">Provv.€</th>
              </tr>
            </thead>
            <tbody>
              {order.items?.map((it, idx) => (
                <tr key={idx} className="border-t border-stone-100">
                  <td className="p-2 font-mono text-xs">{it.code}</td>
                  <td className="p-2">{it.description}{it.warehouse_note && <div className="text-xs text-stone-500 italic">📝 {it.warehouse_note}</div>}</td>
                  <td className="p-2 text-right">{it.quantity} {it.unit}</td>
                  <td className="p-2 text-right">{it.actual_quantity != null ? `${it.actual_quantity} ${it.unit}` : "—"}{it.availability_status === "missing" && <Badge className="ml-1 bg-red-100 text-red-700 text-[10px]">Mancante</Badge>}{it.availability_status === "partial" && <Badge className="ml-1 bg-orange-100 text-orange-700 text-[10px]">Parziale</Badge>}</td>
                  <td className="p-2 text-right">{fm(it.unit_price)}</td>
                  <td className="p-2 text-right font-semibold text-[#8A1C2A]">{fm(((it.actual_line_total ?? it.line_total) || 0))}</td>
                  <td className="p-2 text-right text-xs text-[#C5A059]">{((it.line_commission_rate || 0) * 100).toFixed(2)}%</td>
                  <td className="p-2 text-right text-xs font-semibold text-[#C5A059]">{fm((it.line_commission_amount || 0))}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-stone-50 border-t-2 border-stone-200">
                <td colSpan="5" className="px-3 py-1.5 text-right text-xs">Imponibile (IVA escl.)</td>
                <td className="px-3 py-1.5 text-right font-semibold">{fm(((order.final_subtotal_amount ?? order.subtotal_amount ?? order.total_amount) || 0))}</td>
                <td colSpan="2"></td>
              </tr>
              <tr className="bg-stone-50">
                <td colSpan="5" className="px-3 py-1 text-right text-xs">IVA</td>
                <td className="px-3 py-1 text-right text-sm">{fm(((order.final_vat_total ?? order.vat_total) || 0))}</td>
                <td colSpan="2"></td>
              </tr>
              <tr className="bg-stone-50 border-t border-stone-200">
                <td colSpan="5" className="p-3 text-right font-semibold">Totale (IVA incl.)</td>
                <td className="p-3 text-right text-lg font-bold text-[#8A1C2A]">{fm(((order.final_grand_total ?? order.grand_total ?? order.total_amount) || 0))}</td>
                <td colSpan="2" className="p-3 text-right text-sm font-bold text-[#C5A059]">{fm(((order.final_commission_amount ?? order.commission_amount) || 0))}</td>
              </tr>
            </tfoot>
          </table>
        </Card>

        {/* Confronto ordine originale vs spedito dal magazzino */}
        <OrderOriginalCompare order={order} fm={fm} />

        <ChangeCustomerDialog
          order={order}
          open={changeCustOpen}
          onClose={() => setChangeCustOpen(false)}
          onChanged={() => { onChanged?.(); onClose(); }}
        />
      </DialogContent>
    </Dialog>
  );
};

const OrdersList = () => {
  const { t } = useLang();
  const fm = useFmt();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [chatOrderId, setChatOrderId] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const location = useLocation();
  const [changeReqOrder, setChangeReqOrder] = useState(null);

  const CR_ALLOWED = ["pending", "confirmed", "postponed"];
  const ChangeReqBtn = ({ o }) => (
    CR_ALLOWED.includes(o.status) ? (
      <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); setChangeReqOrder(o); }} className="h-8 px-2 border-amber-400 text-amber-700 hover:bg-amber-50" title="Richiedi modifica al magazzino" data-testid={`change-request-${o.order_number}`}>
        <RefreshCcw className="w-3.5 h-3.5" />
        {(o.change_requests || []).some((r) => r.status === "pending") && <span className="ml-0.5 w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />}
      </Button>
    ) : null
  );


  // Auto-open order detail from ?openOrderId=... / ?openOrderNumber=... (deep-link from chat)
  useEffect(() => {
    if (!orders.length) return;
    const params = new URLSearchParams(location.search);
    const openId = params.get("openOrderId");
    const openNum = params.get("openOrderNumber");
    if (!openId && !openNum) return;
    const found = orders.find((o) => o.id === openId || o.order_number === openNum || o.order_number === openId);
    if (found) setSelected(found);
  }, [orders, location.search]);

  // Debounced fetch on filter/search/reloadKey change. setState only inside async callbacks.
  useEffect(() => {
    let cancelled = false;
    const tm = setTimeout(async () => {
      try {
        const params = new URLSearchParams();
        if (filter !== "all") params.append("status", filter);
        if (search) params.append("search", search);
        const r = await axios.get(`${API}/orders?${params}`);
        if (!cancelled) {
          setOrders(r.data || []);
          setLoading(false);
        }
      } catch {
        if (!cancelled) {
          toast.error(t.pAgent_loadError);
          setLoading(false);
        }
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(tm); };
  }, [filter, search, reloadKey, t.pAgent_loadError]);

  // ---- Azioni sugli ordini provvisori (draft) ----
  const editDraft = (o, e) => {
    e?.stopPropagation?.();
    navigate(`/agent/new-order?draft_id=${o.id}`);
  };
  const submitDraft = async (o, e) => {
    e?.stopPropagation?.();
    if (!window.confirm(`Inviare l'ordine provvisorio ${o.order_number} al magazzino?`)) return;
    try {
      await axios.post(`${API}/orders/${o.id}/submit-draft`);
      toast.success(`Ordine ${o.order_number} inviato al magazzino!`);
      setReloadKey((k) => k + 1);
    } catch (e2) { toast.error(e2.response?.data?.detail || "Errore invio ordine"); }
  };
  const deleteDraft = async (o, e) => {
    e?.stopPropagation?.();
    if (!window.confirm(`Eliminare definitivamente il provvisorio ${o.order_number}?`)) return;
    try {
      await axios.delete(`${API}/orders/${o.id}`);
      toast.success("Provvisorio eliminato");
      setReloadKey((k) => k + 1);
    } catch (e2) { toast.error(e2.response?.data?.detail || "Errore eliminazione"); }
  };

  const DraftActions = ({ o }) => (
    <span className="inline-flex items-center gap-1 flex-wrap">
      <Button size="sm" variant="outline" onClick={(e) => editDraft(o, e)} className="h-8 px-2 border-stone-400 text-stone-700" title="Modifica provvisorio" data-testid={`draft-edit-${o.order_number}`}>
        <Pencil className="w-3.5 h-3.5" />
      </Button>
      <Button size="sm" onClick={(e) => submitDraft(o, e)} className="h-8 px-2 bg-green-600 hover:bg-green-700 text-white" title="Invia al magazzino" data-testid={`draft-submit-${o.order_number}`}>
        <Send className="w-3.5 h-3.5" />
      </Button>
      <Button size="sm" variant="outline" onClick={(e) => deleteDraft(o, e)} className="h-8 px-2 border-red-300 text-red-600 hover:bg-red-50" title="Elimina provvisorio" data-testid={`draft-delete-${o.order_number}`}>
        <Trash2 className="w-3.5 h-3.5" />
      </Button>
    </span>
  );


  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <h1 className="text-2xl sm:text-3xl font-bold text-stone-900 mb-1">I miei ordini</h1>
      <p className="text-stone-500 text-sm mb-6">{orders.length} ordini</p>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <Input placeholder="Cerca..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 bg-white border-stone-300" data-testid="agent-orders-search" />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-full sm:w-56 bg-white border-stone-300"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti</SelectItem>
            {Object.entries(statusLabelMap(t)).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="bg-white border-stone-200 overflow-hidden">
        {loading ? <div className="p-8 text-center"><div className="spinner w-6 h-6 mx-auto" /></div>
          : orders.length === 0 ? <div className="p-8 text-center text-stone-500">Nessun ordine</div>
            : (
              <>
                {/* Desktop table */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-sm min-w-[700px]">
                    <thead className="bg-stone-50 border-b border-stone-200">
                      <tr>
                        <th className="text-left p-3 text-xs font-semibold text-stone-600">N° Ordine</th>
                        <th className="text-left p-3 text-xs font-semibold text-stone-600">Data</th>
                        <th className="text-left p-3 text-xs font-semibold text-stone-600">Cliente</th>
                        <th className="text-center p-3 text-xs font-semibold text-stone-600">Art.</th>
                        <th className="text-right p-3 text-xs font-semibold text-stone-600">Totale</th>
                        <th className="text-right p-3 text-xs font-semibold text-stone-600">Provv.</th>
                        <th className="text-center p-3 text-xs font-semibold text-stone-600">Stato</th>
                        <th className="w-24"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((o) => (
                        <tr key={o.id} className="border-b border-stone-100 hover:bg-stone-50 cursor-pointer" onClick={() => setSelected(o)} data-testid={`agent-order-row-${o.order_number}`}>
                          <td className="p-3 font-mono font-semibold text-[#8A1C2A]">{o.order_number}</td>
                          <td className="p-3 text-xs text-stone-500">{new Date(o.created_at).toLocaleString("it-IT")}</td>
                          <td className="p-3">
                            <div className="font-medium">{o.customer?.customer_name}</div>
                            <div className="text-xs text-stone-500">{o.customer?.company}</div>
                          </td>
                          <td className="p-3 text-center">{o.items?.length || 0}</td>
                          <td className="p-3 text-right">
                            <div className="font-semibold text-[#8A1C2A]">{fm(((o.final_grand_total ?? o.grand_total ?? o.total_amount) || 0))} <span className="text-[9px] font-normal text-stone-400">IVA incl.</span></div>
                            <div className="text-xs text-stone-500">{fm(((o.final_subtotal_amount ?? o.subtotal_amount ?? o.total_amount) || 0))} <span className="text-[9px] text-stone-400">IVA escl.</span></div>
                          </td>
                          <td className="p-3 text-right text-[#C5A059] font-semibold">{fm(((o.final_commission_amount ?? o.commission_amount) || 0))}</td>
                          <td className="p-3 text-center"><Badge className={STATUS_COLOR[o.status]}>{statusLabelMap(t)[o.status]}</Badge></td>
                          <td className="p-3 text-right" onClick={(e) => e.stopPropagation()}>
                            {o.status === "draft" && <DraftActions o={o} />}
                            <ChangeReqBtn o={o} />
                            <OrderRowActions
                              order={o}
                              onOpenDetail={(ord) => setSelected(ord)}
                              onOpenChat={(id) => setChatOrderId(id)}
                              emailEnabled
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Mobile cards */}
                <div className="md:hidden divide-y divide-stone-100">
                  {orders.map((o) => (
                    <div key={o.id} className="p-4 hover:bg-stone-50 cursor-pointer" onClick={() => setSelected(o)} data-testid={`agent-order-card-${o.order_number}`}>
                      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                        <span className="font-mono font-semibold text-[#8A1C2A] text-sm">{o.order_number}</span>
                        <Badge className={STATUS_COLOR[o.status]}>{statusLabelMap(t)[o.status]}</Badge>
                      </div>
                      <p className="text-sm font-medium">{o.customer?.customer_name}</p>
                      <p className="text-xs text-stone-500">{new Date(o.created_at).toLocaleDateString("it-IT")} · {o.items?.length || 0} art.</p>
                      <div className="flex items-center justify-between mt-2 flex-wrap gap-2">
                        <div className="text-right">
                          <div className="font-semibold text-[#8A1C2A]">{fm(((o.final_grand_total ?? o.grand_total ?? o.total_amount) || 0))} <span className="text-[9px] font-normal text-stone-400">IVA incl.</span></div>
                          <div className="text-xs text-stone-500">{fm(((o.final_subtotal_amount ?? o.subtotal_amount ?? o.total_amount) || 0))} <span className="text-[9px] text-stone-400">IVA escl.</span></div>
                        </div>
                        <div onClick={(e) => e.stopPropagation()} className="flex items-center gap-1 flex-wrap">
                          {o.status === "draft" && <DraftActions o={o} />}
                          <ChangeReqBtn o={o} />
                          <OrderRowActions
                            order={o}
                            onOpenDetail={(ord) => setSelected(ord)}
                            onOpenChat={(id) => setChatOrderId(id)}
                            emailEnabled
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
      </Card>

      <OrderDetailDialog order={selected} onClose={() => setSelected(null)} onChanged={() => setReloadKey((k) => k + 1)} />
      {changeReqOrder && (
        <OrderChangeRequestDialog
          order={changeReqOrder}
          onClose={() => setChangeReqOrder(null)}
          onSubmitted={() => setReloadKey((k) => k + 1)}
        />
      )}
      <ChatDrawer open={!!chatOrderId} onClose={() => setChatOrderId(null)} initialOrderId={chatOrderId} />
    </div>
  );
};

export default OrdersList;
